/**
 * AccountDialogLayer — the account / AI-flow dialog layer, hosted by whichever route is showing.
 *
 * These five surfaces are raised by STORE FLAGS written from code that has no idea which route is
 * on screen: generateGate.ts's paywall fork (welcome-depletion vs token-purchase), the sign-in
 * buttons, useAiWorkspaceFlow's showAiError() when a generation fails, and the purchase-success
 * confirmation that TokenPurchaseDialog / WelcomeDepletionDialog raise via showIapSuccess() on the
 * way out. Until 2026-08-21 their only mount point was Transport, which lives inside TimelineView —
 * sound while every AI surface forced the Timeline route first (enterAiWorkspaceFromBoard did
 * exactly that), and a dead end the moment the board grew its own in-place Enhance dialog, which
 * deliberately does NOT hop the route. The flags were then set with nothing to render them: the
 * paywall made the dialog vanish with no explanation, and a failed generation was completely silent
 * — worse, aiErrorDialog stayed set, and SceneBoardPage's escOverlayOpen() reads it, so the
 * close-project Escape rung was swallowed for the rest of the page's life.
 *
 * IapSuccessDialog joined the layer on 2026-08-22 for the same reason, one step later in the same
 * flow: iapSuccessInfo is a plain module-level Zustand field that nothing clears on a route change,
 * so buying credits from the board's Enhance dialog set it, found no renderer, and then ambushed
 * the user with a stale "purchase complete" the next time they opened the Timeline (owner report).
 * The two dialogs that SET the flag already live here, so the confirmation belongs here too — and
 * Transport's own copy was REMOVED in the same change, since Transport mounts this layer.
 *
 * Mounted by Transport and by SceneBoardPage, and exactly one of the two is alive at a time.
 * Inside SceneBoardPage that is by construction: its route ternary mounts TimelineView (and hence
 * Transport) only for 'timeline', and its own mount is gated on the complement. Outside it there is
 * no SceneBoardPage at all — App renders TimelineView directly on the embedded non-board path
 * (App.tsx:360) and on the standalone web path (App.tsx:456) — so Transport is the sole host there.
 * Either way the count is one, which is why a render of any of these surfaces in Transport ITSELF
 * would be a duplicate; transportIapSuccessOneCopy.test.tsx counts the DOM to keep that honest.
 *
 * Transport.module.css is the aiError block's home and stays that way: the same classes are still
 * used by Transport's own subscription gate, and duplicating them into a new module would leave two
 * copies of one look to drift. IapSuccessDialog keeps its own module (z-index 120, above the
 * paywall's 100 and RewardDialog's 110) — it is NOT portalled, so it relies on the host's root
 * stacking context: SceneBoardPage's .workspace is position:relative with no z-index/transform, so
 * it creates none, which is why the paywall at z 100 already paints correctly from there.
 */
import { useTranslation } from 'react-i18next';
import { useUiStore } from '@/store/uiStore.ts';
import { useAccountStore } from '@/store/accountStore.ts';
import { AuthDialog } from './AuthDialog.tsx';
import { TokenPurchaseDialog } from './TokenPurchaseDialog.tsx';
import { WelcomeDepletionDialog } from './WelcomeDepletionDialog.tsx';
import { IapSuccessDialog } from './IapSuccessDialog.tsx';
import styles from './Transport.module.css';

export function AccountDialogLayer() {
  const { t } = useTranslation('transport');
  const aiErrorDialog = useUiStore(s => s.aiErrorDialog);
  const closeAiError = useUiStore(s => s.closeAiError);
  const authDialogOpen = useAccountStore(s => s.authDialogOpen);
  const setAuthDialogOpen = useAccountStore(s => s.setAuthDialogOpen);
  const tokenPurchaseDialogOpen = useAccountStore(s => s.tokenPurchaseDialogOpen);
  const setTokenPurchaseDialogOpen = useAccountStore(s => s.setTokenPurchaseDialogOpen);
  const welcomeDepletionOpen = useAccountStore(s => s.welcomeDepletionOpen);
  const setWelcomeDepletionDismissed = useAccountStore(s => s.setWelcomeDepletionDismissed);
  const iapSuccessInfo = useAccountStore(s => s.iapSuccessInfo);
  const dismissIapSuccess = useAccountStore(s => s.dismissIapSuccess);

  return (
    <>
      {aiErrorDialog && (
        <div className={styles.aiErrorOverlay} onClick={closeAiError}>
          <div className={styles.aiErrorDialog} onClick={e => e.stopPropagation()}>
            <div className={styles.aiErrorIcon}>&#x26A0;&#xFE0F;</div>
            <div className={styles.aiErrorTitle}>{aiErrorDialog.title}</div>
            <div className={styles.aiErrorMessage}>{aiErrorDialog.message}</div>
            <div className={styles.aiErrorCredit}>{t('aiErrorNoCreditsDeducted')}</div>
            {/* Both dismiss paths clear aiErrorDialog — required, not incidental: the flag is one
                of escOverlayOpen()'s members, so leaving it set wedges the board's Escape ladder. */}
            <button className={styles.aiErrorDismiss} onClick={closeAiError}>{t('aiErrorDismissButton')}</button>
          </div>
        </div>
      )}
      {authDialogOpen && <AuthDialog open onClose={() => setAuthDialogOpen(false)} />}
      {tokenPurchaseDialogOpen && <TokenPurchaseDialog open onClose={() => setTokenPurchaseDialogOpen(false)} />}
      {welcomeDepletionOpen && (
        <WelcomeDepletionDialog
          open
          onClose={setWelcomeDepletionDismissed}
        />
      )}
      {/* Last in the list on purpose: both dialogs above call showIapSuccess() then close
          themselves, so the confirmation replaces them and must paint over anything that has not
          finished unmounting. z-index 120 does that on its own; the source order keeps it true if
          a future sibling lands on the same z. */}
      {iapSuccessInfo && <IapSuccessDialog info={iapSuccessInfo} onDismiss={dismissIapSuccess} />}
    </>
  );
}
